/**
 * SFTP connection contract (integration-service `SFTPConfig` / `SFTPCredentials`
 * and `POST /connections/{id}/files/validate`).
 *
 * Casing is the trap here: everything stored inside `config` is snake_case (the
 * handler's Pydantic model has no aliases), while the `files/validate` body is
 * camelCase. The two entry shapes below are kept separate on purpose so a
 * wrong-case key is a type error instead of a silently dropped field.
 */

/** CLIENT dials out to the customer's host; LILY is Lily's managed server. */
export type SftpHostingMode = 'CLIENT' | 'LILY';

/** What a typed file entry is for. */
export type SftpFileType = 'INPUT' | 'OUTPUT' | 'IMAGES' | 'TAXONOMY' | 'CLIENT_VERIFICATION';

/** Every entry type, in the order the editor offers them. */
export const SFTP_FILE_TYPES: readonly SftpFileType[] = [
  'INPUT',
  'OUTPUT',
  'IMAGES',
  'TAXONOMY',
  'CLIENT_VERIFICATION',
];

/** The single placeholder an OUTPUT `file_name` may carry. */
export const SUBSIDIARY_CODE_PLACEHOLDER = '{subsidiary_code}';

/** Stored shape (snake_case) — sent inside `config.files` on create / PATCH. */
export interface SftpFileConfig {
  type: SftpFileType;
  /** Absolute directory the entry lives in. */
  path: string;
  file_name?: string | null;
  pattern?: string | null;
  /** Subset check for CSV/TSV INPUT files. */
  expected_headers?: string[] | null;
}

/** `config` of an SFTP connection. */
export interface SftpConnectionConfig {
  mode: SftpHostingMode;
  /** Omitted for LILY — injected server-side. */
  host?: string;
  /** Defaults to 22. */
  port?: number;
  base_path: string;
  /** Legacy glob — superseded by `files[]`. */
  file_pattern?: string | null;
  files?: SftpFileConfig[];
  /** False → delivery triggers answer 409 `DELIVERY_DISABLED`. */
  delivery_enabled?: boolean;
}

/** `credentials` of an SFTP connection — at least one of password / private_key. */
export interface SftpCredentials {
  username: string;
  password?: string;
  private_key?: string;
  private_key_passphrase?: string;
}

/** One entry on the `files/validate` wire (camelCase). */
export interface SftpFileConfigWire {
  type: SftpFileType;
  path: string;
  fileName?: string | null;
  pattern?: string | null;
  expectedHeaders?: string[] | null;
}

/** Body for `files/validate`. Omit `files` to check what is saved. */
export interface ConnectionFilesValidateRequest {
  files?: SftpFileConfigWire[];
}

/** Per-entry verdict from `files/validate`. */
export interface FileConfigValidationItem {
  type: SftpFileType;
  path: string;
  fileName: string | null;
  pattern: string | null;
  exists: boolean;
  isDirectory: boolean;
  matchedCount: number;
  headers: string[];
  missingHeaders: string[];
  isValid: boolean;
  message: string;
}

/** `data` of `files/validate`. `isReady` means "has a valid INPUT" only. */
export interface ConnectionFilesValidateResponse {
  isReady: boolean;
  files: FileConfigValidationItem[];
  missingRequiredTypes: string[];
  summary: string;
}

/** Body for `POST /connections/test` — same shape as create, nothing stored. */
export interface ConnectionPreSaveTestRequest {
  type: string;
  config: Record<string, unknown>;
  credentials?: Record<string, unknown>;
}

/** `data` of `POST /connections/test`. A failed check is still a 200. */
export interface ConnectionPreSaveTestResult {
  type: string;
  isValid: boolean;
  message: string;
  errorCode: string | null;
  details: Record<string, unknown> | null;
}

/** One placeholder an output file name may carry (`GET connectors/{code}/file-naming`). */
export interface FileNamePlaceholder {
  /** Bare name, e.g. `date`. */
  name: string;
  /** As written in a name, e.g. `{date}`. */
  token: string;
  description: string;
  example: string;
}

/** Server verdict + rendered example for one candidate name (`?template=`). */
export interface FileNamePreview {
  template: string;
  isValid: boolean;
  /** Readable reason when invalid — the same one a save would 422 with. */
  error: string | null;
  example: string | null;
}

/** `data` of `GET connectors/{code}/file-naming`. */
export interface FileNamingInfo {
  connectorCode: string;
  field: string;
  placeholders: FileNamePlaceholder[];
  rules: string[];
  exampleTemplate: string;
  example: string;
  /** Null unless `template` was sent. */
  preview: FileNamePreview | null;
}
